import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { money, paymentReads, plans as plansApi } from '../../api/fee'
import { BuyCredit } from '../../components/BuyCredit'
import { ConfirmButton, Notice, PageTitle, QueryState, Tabs } from '../../components/common'
import { PaymentMethodSummary } from '../../components/payment'
import { PayActions } from '../../components/PayActions'
import { useProject } from '../../layout/projectContext'
import { formatPrice } from '../../lib/format'
import { hasMethod, paymentMethodTone } from '../../lib/paymentHelpers'
import { useMoneyAction } from '../../lib/useMoneyAction'

/**
 * Port of `PaymentMethodViewComponent` (`projects/:projectId/payment/method`)
 * + its embedded `app-paymentmethod-change`: the saved card (brand, last4,
 * expiry warnings), "Change Payment method" (pay for a plan or buy credit
 * through Checkout, saving the new card), and "Remove payment method and
 * cancel all active subscriptions" (`PATCH …/payment/cancel`).
 */
export function PaymentMethodPage() {
  const { projectId } = useProject()
  const pm = useApiQuery(() => paymentReads.method(projectId), [projectId])
  const planList = useApiQuery(() => plansApi.search(projectId), [projectId])
  const [changing, setChanging] = useState(false)
  const [tab, setTab] = useState<'plan' | 'credit'>('plan')
  const actions = useMoneyAction()
  const base = `/projects/${encodeURIComponent(projectId)}`
  const payablePlans = (planList.data ?? []).filter((p) => Number(p.price) > 0)

  const remove = async () => {
    const r = await actions.run('remove', 'remove', () => money.cancelPayment(projectId))
    if (r !== undefined) {
      actions.setFeedback('remove', { tone: 'success', text: 'Payment method removed, subscriptions canceled.' })
      pm.refetch()
    }
  }

  return (
    <div className="portal-card">
      <PageTitle title="Payment method" />
      <QueryState loading={pm.loading} error={pm.error}>
        {pm.data && !hasMethod(pm.data) && (
          <div className="fs-panel fs-panel--warning">
            <p>{pm.data.createdAt ? 'Payment method removed.' : 'Payment method not set.'}</p>
            <p>
              A payment method is saved when you <Link to={`${base}/subscriptions`}>start a subscription</Link>,{' '}
              <Link to={`${base}/products`}>pay for a product</Link> or <Link to={`${base}/payment/credit`}>buy credit</Link>{' '}
              with a new payment method.
            </p>
          </div>
        )}
        {pm.data && hasMethod(pm.data) && (
          <div className={`fs-panel ${paymentMethodTone(pm.data)}`}>
            <PaymentMethodSummary pm={pm.data} />
            {pm.data.isLive === 0 && <p className="portal-muted-small">Test mode</p>}
            <div className="portal-inline-actions">
              <button type="button" onClick={() => setChanging(!changing)} aria-expanded={changing}>
                Change Payment method
              </button>
              <ConfirmButton
                label="Remove payment method and cancel all active subscriptions"
                pendingText="Are you sure you want to remove current payment method and cancel all subscriptions?"
                busy={actions.isBusy('remove')}
                className="portal-button-danger"
                onConfirm={() => void remove()}
              />
            </div>
          </div>
        )}
        <Notice feedback={actions.feedbackFor('remove')} />
        {changing && pm.data && (
          <section aria-label="Change payment method">
            <Tabs
              tabs={[
                ...(payablePlans.length ? [{ id: 'plan' as const, label: 'Pay for a plan' }] : []),
                { id: 'credit' as const, label: 'Buy credit' },
              ]}
              active={payablePlans.length ? tab : 'credit'}
              onChange={setTab}
            />
            {payablePlans.length > 0 && tab === 'plan' ? (
              <ul className="fs-plain-list">
                {payablePlans.map((p) => (
                  <li key={p.id} className="fs-panel">
                    <strong>{p.name}</strong> — {formatPrice(p.price, p.currency)}
                    <PayActions
                      projectId={projectId}
                      target={{ type: 'plan', plans: p.id }}
                      hasPaymentMethod={hasMethod(pm.data)}
                      onCharged={() => pm.refetch()}
                      newMethodOnly
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <BuyCredit projectId={projectId} hasPaymentMethod={hasMethod(pm.data)} onCharged={() => pm.refetch()} newMethodOnly />
            )}
          </section>
        )}
      </QueryState>
    </div>
  )
}
